import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAgendamentoStepIndicator } from "../../../hooks/useAgendamentoStepIndicator";
import { isAgendamentoLotePath } from "../../../hooks/useAgendamentoStepIndicator/agendamentoV2Steps";
import { useItineranteLista } from "../../../hooks/queries/itinerante/useItineranteLista/useItineranteLista";
import { useCadastrarAgendamento } from "../../../hooks/queries/agendamento/useCadastrarAgendamento/useCadastrarAgendamento";
import { useVincularAgendamentoCliente } from "../../../hooks/queries/agendamento/useVincularAgendamentoCliente/useVincularAgendamentoCliente";
import { useCadastrarLoteAgendamento } from "../../../hooks/queries/loteAgendamento/useCadastrarLoteAgendamento/useCadastrarLoteAgendamento";
import { useVincularItineranteLoteAgendamento } from "../../../hooks/queries/loteAgendamento/useVincularItineranteLoteAgendamento/useVincularItineranteLoteAgendamento";
import { useContextSite } from "../../../context/Context";
import { useUserPermissions } from "../../../hooks/useUserPermissions";
import { useBrowserSessionStorage } from "../../../hooks/useBrowserSessionStorage";
import { Tracking } from "../../../services/Tracking";
import { IItineranteListProps } from "../../../types/itinerante";
import { IPagination } from "../../../types/pagination";
import { DEFAULT_PAGE_SIZE } from "../../../constants/pagination";
import { TipoClienteEnum } from "../../../enums/tipoCliente";

export const useItineranteV2 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const stepIndicator = useAgendamentoStepIndicator();
  const { setIsLoad } = useContextSite();
  const { userStorageData, isUserType } = useUserPermissions();
  const [uuidClienteAgendamento, , removeUuidClienteAgendamento] =
    useBrowserSessionStorage("@uuidClienteAgendamento");

  const isDespachante = isUserType(TipoClienteEnum.DESPACHANTE);
  const isLoteAgendamento =
    isAgendamentoLotePath(location.pathname) && isDespachante;

  const [isFiltroAberto, setIsFiltroAberto] = useState(false);
  const [numberPage, setNumberPage] = useState(0);
  const [filtro, setFiltro] = useState<IItineranteListProps>({});

  const {
    data: itinerantesPagina,
    isLoading,
    isError,
    error,
  } = useItineranteLista({
    ...filtro,
    page: numberPage,
    size: DEFAULT_PAGE_SIZE,
  });

  const itinerantes = itinerantesPagina?.content || [];
  const pagination: IPagination = {
    actualPage: itinerantesPagina?.number ?? 0,
    totalPage: itinerantesPagina?.totalPages ?? 0,
    totalRegister: itinerantesPagina?.totalElements ?? 0,
  };

  const { mutate: cadastrarAgendamento } = useCadastrarAgendamento();
  const { mutate: vincularAgendamentoCliente } =
    useVincularAgendamentoCliente();
  const { mutate: cadastrarLoteAgendamento } = useCadastrarLoteAgendamento();
  const { mutate: vincularItineranteLoteAgendamento } =
    useVincularItineranteLoteAgendamento();

  function handleToggleFiltro() {
    setIsFiltroAberto((prev) => !prev);
    Tracking.userAction("mapa:agendamento/itinerantes:clique-filtrar");
  }

  function handleFiltrar(data: IItineranteListProps) {
    setFiltro(data);
    setNumberPage(0);
  }

  function submitAgendamento(uuidItinerante: string) {
    if (isLoteAgendamento) {
      setIsLoad(true);
      cadastrarLoteAgendamento(
        {
          tipoAtendimento: "ITINERANTE",
          uuidDespachanteResponsavel: userStorageData?.uuidCliente ?? "",
        },
        {
          onSuccess: ({ data: loteAgendamento }) => {
            vincularItineranteLoteAgendamento(
              { uuid: loteAgendamento.uuid, uuidItinerante },
              {
                onSuccess: () => {
                  navigate(
                    `/agendamento-lote/${loteAgendamento.uuid}/servicos`,
                  );
                },
                onError: ({
                  response: {
                    data: { mensagem },
                  },
                }: any) => {
                  Tracking.error(
                    "mapa:agendamento-lote/itinerantes:vincular-itinerante-falhou",
                    { mensagem },
                  );
                },
                onSettled: () => setIsLoad(false),
              },
            );
          },
          onError: ({
            response: {
              data: { mensagem },
            },
          }: any) => {
            Tracking.error(
              "mapa:agendamento-lote/itinerantes:criar-lote-agendamento-falhou",
              { mensagem },
            );
            setIsLoad(false);
          },
        },
      );
      return;
    }

    const clienteSelecionadoParaAgendamento = Boolean(uuidClienteAgendamento);
    const uuidCliente = clienteSelecionadoParaAgendamento
      ? uuidClienteAgendamento
      : userStorageData?.uuidCliente;

    setIsLoad(true);
    cadastrarAgendamento(
      { uuidItinerante },
      {
        onSuccess: ({ data }) => {
          vincularAgendamentoCliente(
            { uuid: data.uuid, uuidCliente },
            {
              onSuccess: () => {
                removeUuidClienteAgendamento();
                navigate(`/agendamento/${data.uuid}/servicos`);
              },
              onError: ({
                response: {
                  data: { mensagem },
                },
              }: any) => {
                Tracking.error(
                  "mapa:agendamento/itinerantes:vincular-cliente-falhou",
                  { mensagem },
                );
              },
              onSettled: () => setIsLoad(false),
            },
          );
        },
        onError: ({
          response: {
            data: { mensagem },
          },
        }: any) => {
          Tracking.error(
            "mapa:agendamento/itinerantes:criar-agendamento-falhou",
            { mensagem },
          );
          setIsLoad(false);
        },
      },
    );
  }

  useEffect(() => {
    if (!isError) return;
    setIsLoad(true);

    const {
      response: {
        data: { mensagem },
      },
    } = error as any;

    Tracking.error("mapa:agendamento/itinerantes:buscar-itinerantes-falhou", {
      mensagem,
    });
  }, [isError]);

  useEffect(() => {
    if (isLoading) {
      setIsLoad(true);
      return;
    }
    setIsLoad(false);
  }, [isLoading]);

  return {
    stepIndicator,
    isFiltroAberto,
    handleToggleFiltro,
    handleFiltrar,
    itinerantes,
    isLoading,
    pagination,
    setNumberPage,
    submitAgendamento,
  };
};
